// ClaimPagesDashboard.tsx
import React, { useEffect, useState, useContext, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
  Link,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import PendingIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import AssignmentIcon from '@mui/icons-material/Assignment'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import TagIcon from '@mui/icons-material/Tag'
import WorkIcon from '@mui/icons-material/Work'
import BadgeIcon from '@mui/icons-material/Badge'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import UserService from '../../services/UserService/UserService'
import { formatAbsoluteDate, formatRelativeDate } from '../../utils/formatRelativeDate'
import { UserDTO } from '../../models/UserDTO.model'

import { ClaimedPage } from '../../services/ClaimPages/GetClaimedPage'
import { ClaimedPagesService } from '../../services/ClaimPages/GetClaimedPage'
import { AcceptedClaimPagesByAdminService } from '../../services/ClaimPages/GetAcceptedClaimPage'
import { RejectedClaimPagesService } from '../../services/ClaimPages/GetRejectedClaimPage'
import { WithdrawnClaimPagesService } from '../../services/ClaimPages/GetWithdrawnClaimPage'
import { AcceptClaimPageService } from '../../services/ClaimPages/AcceptClaimPage'
import { ClaimStatusTypes } from '../../models/ClaimStatus.model'
import { RejectClaimPageService } from '../../services/ClaimPages/RejectClaimPage'
import { NotificationContext } from '../../providers/NotificationContext'

type StatusTab = 'claimed' | 'accepted' | 'rejected' | 'withdrawn'

const getClaimCardTimestamp = (page: ClaimedPage, tab: StatusTab): string | undefined => {
  if (tab === 'claimed') {
    return page.createdAt || undefined
  }
  return page.reviewedAt || undefined
}

const getClaimProofLink = (proof?: Record<string, unknown> | null): string | null => {
  if (!proof) return null
  const linkedinUrl = proof.linkedinUrl
  if (typeof linkedinUrl === 'string' && linkedinUrl.trim()) return linkedinUrl.trim()
  const documentation = proof.documentation
  if (typeof documentation === 'string' && documentation.trim()) return documentation.trim()
  return null
}

// ── Status config ──
const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; gradient: string; icon: JSX.Element; bgLight: string }
> = {
  claimed: {
    label: 'Pending',
    color: '#7C4DFF',
    gradient: 'linear-gradient(135deg, #7C4DFF 0%, #B388FF 100%)',
    icon: <PendingIcon />,
    bgLight: '#EDE7F6',
  },
  accepted: {
    label: 'Approved',
    color: '#00C853',
    gradient: 'linear-gradient(135deg, #00C853 0%, #69F0AE 100%)',
    icon: <CheckCircleIcon />,
    bgLight: '#E8F5E9',
  },
  rejected: {
    label: 'Rejected',
    color: '#FF1744',
    gradient: 'linear-gradient(135deg, #FF1744 0%, #FF8A80 100%)',
    icon: <CancelOutlinedIcon />,
    bgLight: '#FFEBEE',
  },
  withdrawn: {
    label: 'Withdrawn',
    color: '#546E7A',
    gradient: 'linear-gradient(135deg, #546E7A 0%, #90A4AE 100%)',
    icon: <RemoveCircleOutlineIcon />,
    bgLight: '#ECEFF1',
  },
}

const ClaimPagesDashboard = (): JSX.Element => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const type = searchParams.get('type') // "hospital" or "agency"
  const isAgency = type === 'agency'
  const navigate = useNavigate()
  const { refreshNotifications } = useContext(NotificationContext)

  const initialStatus = (['claimed', 'accepted', 'rejected', 'withdrawn'].includes(searchParams.get('status') || '')
    ? searchParams.get('status')!
    : 'claimed') as StatusTab
  const [statusTab, setStatusTab] = useState<StatusTab>(initialStatus)
  const [claimedPages, setClaimedPages] = useState<ClaimedPage[]>([])
  const [acceptedPages, setAcceptedPages] = useState<ClaimedPage[]>([])
  const [rejectedPages, setRejectedPages] = useState<ClaimedPage[]>([])
  const [withdrawnPages, setWithdrawnPages] = useState<ClaimedPage[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [selectedPage, setSelectedPage] = useState<ClaimedPage | null>(null)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })
  const [actionLoading, setActionLoading] = useState<{ pageId: number; action: 'approve' | 'reject' } | null>(null)
  const [claimerDialogOpen, setClaimerDialogOpen] = useState(false)
  const [claimerUser, setClaimerUser] = useState<UserDTO | null>(null)
  const [claimerLoading, setClaimerLoading] = useState(false)
  const [claimerError, setClaimerError] = useState<string | null>(null)

  const entityLabel = isAgency ? 'Agency' : 'Hospital'

  const handleOpenClaimerDetails = async (page: ClaimedPage) => {
    setClaimerDialogOpen(true)
    setClaimerUser(null)
    setClaimerError(null)

    if (!page.auth0UserId) {
      setClaimerError('User profile is not available for this claim.')
      return
    }

    setClaimerLoading(true)
    try {
      const user = await UserService.getUser(page.auth0UserId)
      setClaimerUser(user)
    } catch (error) {
      console.error('Failed to fetch claimer user:', error)
      setClaimerError('Could not load user profile.')
    } finally {
      setClaimerLoading(false)
    }
  }

  const handleCloseClaimerDialog = () => {
    setClaimerDialogOpen(false)
    setClaimerUser(null)
    setClaimerError(null)
  }

  // Fetch all pages
  const fetchAllPages = async () => {
    setLoading(true)
    try {
      const [claimed, accepted, rejected, withdrawn] = await Promise.all([
        new ClaimedPagesService().getClaimedPages(isAgency).catch(() => []),
        new AcceptedClaimPagesByAdminService().getAcceptedClaimPages(isAgency).catch(() => []),
        new RejectedClaimPagesService().getRejectedClaimPages(isAgency).catch(() => []),
        new WithdrawnClaimPagesService().getWithdrawnClaimPages(isAgency).catch(() => []),
      ])
      setClaimedPages(claimed)
      setAcceptedPages(accepted)
      setRejectedPages(rejected)
      setWithdrawnPages(withdrawn)
    } catch (error) {
      console.error('Failed to fetch claimed pages:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllPages()
    refreshNotifications()
    setSearchQuery('')
  }, [isAgency])

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })
  const handleOpenDialog = (page: ClaimedPage) => {
    setSelectedPage(page)
    setDialogOpen(true)
  }
  const handleCloseDialog = () => {
    setSelectedPage(null)
    setDialogOpen(false)
  }

  // Accept a claimed page
  const handleApprove = async (pageId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    setActionLoading({ pageId, action: 'approve' })
    try {
      await new AcceptClaimPageService().acceptClaimPage(pageId, isAgency)
      setSnackbar({ open: true, message: 'Request approved successfully', severity: 'success' })
      await fetchAllPages()
      handleCloseDialog()
    } catch (error) {
      console.error(error)
      setSnackbar({ open: true, message: 'Failed to approve request', severity: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  // Reject a claimed page
  const handleReject = async (pageId: number, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    setActionLoading({ pageId, action: 'reject' })
    try {
      await new RejectClaimPageService().rejectClaimedPage(pageId, ClaimStatusTypes.REJECTED, isAgency)
      setSnackbar({ open: true, message: 'Request rejected successfully', severity: 'success' })
      await fetchAllPages()
      handleCloseDialog()
    } catch (error) {
      console.error(error)
      setSnackbar({ open: true, message: 'Failed to reject request', severity: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  const matchesSearch = (page: ClaimedPage, query: string): boolean => {
    if (!query.trim()) return true
    const lowerQuery = query.toLowerCase()
    if (page.id.toString().includes(lowerQuery)) return true
    if (page.name?.toLowerCase().includes(lowerQuery)) return true
    if (page.city?.toLowerCase().includes(lowerQuery)) return true
    if (page.state?.toLowerCase().includes(lowerQuery)) return true
    if (page.claimId?.toString().includes(lowerQuery)) return true
    if (page.requesterEmail?.toLowerCase().includes(lowerQuery)) return true
    if (page.requesterName?.toLowerCase().includes(lowerQuery)) return true
    return false
  }

  // ── Counts per status ──
  const counts = useMemo(
    () => ({
      claimed: claimedPages.length,
      accepted: acceptedPages.length,
      rejected: rejectedPages.length,
      withdrawn: withdrawnPages.length,
    }),
    [claimedPages, acceptedPages, rejectedPages, withdrawnPages],
  )

  const totalCount = counts.claimed + counts.accepted + counts.rejected + counts.withdrawn

  // ── Current tab pages + search ──
  const getCurrentPages = (): ClaimedPage[] => {
    let pages: ClaimedPage[] = []
    switch (statusTab) {
      case 'claimed':
        pages = claimedPages
        break
      case 'accepted':
        pages = acceptedPages
        break
      case 'rejected':
        pages = rejectedPages
        break
      case 'withdrawn':
        pages = withdrawnPages
        break
    }
    if (searchQuery.trim()) {
      pages = pages.filter((page) => matchesSearch(page, searchQuery))
    }
    return pages
  }

  const pages = getCurrentPages()
  const activeConfig = STATUS_CONFIG[statusTab]

  return (
    <Box sx={{ pb: 4 }}>
      {/* ════════════ Hero Banner ════════════ */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: isAgency
            ? 'linear-gradient(135deg, #00695C 0%, #26A69A 50%, #4DB6AC 100%)'
            : 'linear-gradient(135deg, #007C91 0%, #00ACC1 50%, #26C6DA 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', right: -30, top: -30, opacity: 0.07 }}>
          <AssignmentIcon sx={{ fontSize: 240 }} />
        </Box>
        <Box sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
              {entityLabel} Claim Requests
            </Typography>
            <Chip
              label={entityLabel}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 700,
                backdropFilter: 'blur(8px)',
              }}
            />
          </Stack>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Manage all {entityLabel.toLowerCase()} page claim requests and their statuses
          </Typography>
          <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <AssignmentIcon sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {totalCount} Total Requests
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {counts.claimed} Awaiting Review
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* ════════════ Status Stat Cards (tabs) ════════════ */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {(['claimed', 'accepted', 'rejected', 'withdrawn'] as const).map((key) => {
          const cfg = STATUS_CONFIG[key]
          const count = counts[key]
          const isActive = statusTab === key
          return (
            <Grid item xs={6} sm={3} key={key}>
                <Card
                  onClick={() => {
                    setStatusTab(key)
                    setSearchQuery('')
                  }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: { xs: 2.5, sm: 3 },
                    border: isActive ? `2px solid ${cfg.color}` : '1px solid rgba(0,0,0,0.06)',
                    boxShadow: isActive ? `0 4px 20px ${cfg.color}25` : '0 1px 6px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${cfg.color}22`,
                    },
                  }}
                >
                  <Box sx={{ height: 4, background: cfg.gradient }} />
                  <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: cfg.color,
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            fontSize: { xs: '0.55rem', sm: '0.75rem' },
                          }}
                        >
                          {cfg.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: '#1A2027',
                            lineHeight: 1.1,
                            mt: 0.5,
                            fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
                          }}
                        >
                          {loading ? '—' : count}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          width: { xs: 36, sm: 48 },
                          height: { xs: 36, sm: 48 },
                          bgcolor: isActive ? cfg.color : cfg.bgLight,
                          color: isActive ? 'white' : cfg.color,
                          transition: 'all 0.3s ease',
                          display: { xs: 'none', sm: 'flex' },
                        }}
                      >
                        {React.cloneElement(cfg.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                      </Avatar>
                    </Stack>
                    {/* Clickable hint */}
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: isActive ? cfg.color : '#B0BEC5',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                        {isActive ? 'Selected' : 'View details'}
                      </Typography>
                      {!isActive && <OpenInNewIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                    </Box>
                  </CardContent>
                </Card>
            </Grid>
          )
        })}
      </Grid>

      {/* ════════════ Search Bar ════════════ */}
      <TextField
        fullWidth
        placeholder={`Search ${entityLabel.toLowerCase()} requests by name, city, state, claimer email, or ID…`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            '& fieldset': { borderColor: 'rgba(0,124,145,0.12)' },
            '&:hover fieldset': { borderColor: 'rgba(0,124,145,0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#007C91' },
          },
        }}
      />

      {/* ════════════ Results Header ════════════ */}
      {!loading && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mb: 2,
            px: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {activeConfig.label} Requests
            </Typography>
            <Chip
              label={`${pages.length} result${pages.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: `${activeConfig.color}18`, color: activeConfig.color, fontWeight: 700, fontSize: '0.75rem' }}
            />
          </Stack>
        </Stack>
      )}

      {/* ════════════ Requests List ════════════ */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: activeConfig.color }} />
        </Box>
      ) : pages.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            background: 'linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 100%)',
            borderRadius: 4,
            border: '2px dashed rgba(0,0,0,0.08)',
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: activeConfig.bgLight,
              color: activeConfig.color,
              mx: 'auto',
              mb: 2.5,
            }}
          >
            {React.cloneElement(activeConfig.icon, { sx: { fontSize: 36 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', mb: 1 }}>
            {searchQuery.trim() ? 'No Matching Requests' : `No ${activeConfig.label} Requests`}
          </Typography>
          <Typography variant="body2" sx={{ color: '#78909C', maxWidth: 420, mx: 'auto' }}>
            {searchQuery.trim()
              ? `No requests matching "${searchQuery.trim()}" were found.`
              : `There are currently no ${activeConfig.label.toLowerCase()} claim requests for ${entityLabel.toLowerCase()} pages.`}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {pages.map((page) => {
            const cardTimestamp = getClaimCardTimestamp(page, statusTab)
            const cardTimestampLabel = cardTimestamp ? formatRelativeDate(cardTimestamp) : ''
            return (
            <Grid item xs={12} sm={6} key={page.id}>
              <Card
                onClick={() => handleOpenDialog(page)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${activeConfig.color}`,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 6px 20px ${activeConfig.color}18`,
                    '& .card-actions': { opacity: 1 },
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    {/* Left: Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Name */}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: '#1A2027',
                          lineHeight: 1.3,
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {page.name || 'Unnamed Page'}
                      </Typography>

                      {/* Location */}
                      {(page.city || page.state) && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: page.requesterEmail ? 0.75 : 1.5 }}>
                          <LocationOnIcon sx={{ fontSize: 15, color: '#90A4AE' }} />
                          <Typography variant="body2" sx={{ color: '#78909C' }}>
                            {[page.city, page.state].filter(Boolean).join(', ')}
                          </Typography>
                        </Stack>
                      )}

                      {page.requesterEmail && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                          <EmailIcon sx={{ fontSize: 15, color: '#90A4AE' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#5C6BC0',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {page.requesterEmail}
                          </Typography>
                        </Stack>
                      )}

                      {/* Meta chips */}
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={`#${page.id}`}
                          sx={{
                            bgcolor: '#F5F5F5',
                            color: '#78909C',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            '& .MuiChip-icon': { color: '#B0BEC5' },
                          }}
                        />
                        {page.claimId != null && page.claimId > 0 && (
                          <Chip
                            size="small"
                            label={`Claim #${page.claimId}`}
                            sx={{
                              bgcolor: `${activeConfig.color}12`,
                              color: activeConfig.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={entityLabel}
                          sx={{
                            bgcolor: isAgency ? '#E0F7FA' : '#E0F2F1',
                            color: isAgency ? '#00838F' : '#00695C',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      </Stack>

                      {cardTimestampLabel && (
                        <Typography variant="caption" sx={{ color: '#B0BEC5', fontSize: '0.7rem', mt: 0.75, display: 'block' }}>
                          <AccessTimeIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
                          {cardTimestampLabel}
                        </Typography>
                      )}
                    </Box>

                    {/* Right: Action Buttons or Status icon */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      {statusTab === 'claimed' ? (
                        <Stack
                          className="card-actions"
                          direction="row"
                          spacing={0.75}
                          sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.25s ease' }}
                        >
                          <Tooltip title="Reject" arrow>
                            <IconButton
                              size="small"
                              disabled={actionLoading?.pageId === page.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleReject(page.id)
                              }}
                              sx={{
                                bgcolor: '#FFEBEE',
                                color: '#FF1744',
                                width: 36,
                                height: 36,
                                '&:hover': { bgcolor: '#FF1744', color: 'white' },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {actionLoading?.pageId === page.id && actionLoading?.action === 'reject' ? (
                                <CircularProgress size={16} sx={{ color: '#FF1744' }} />
                              ) : (
                                <CancelIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve" arrow>
                            <IconButton
                              size="small"
                              disabled={actionLoading?.pageId === page.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleApprove(page.id)
                              }}
                              sx={{
                                bgcolor: '#E8F5E9',
                                color: '#00C853',
                                width: 36,
                                height: 36,
                                '&:hover': { bgcolor: '#00C853', color: 'white' },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {actionLoading?.pageId === page.id && actionLoading?.action === 'approve' ? (
                                <CircularProgress size={16} sx={{ color: '#00C853' }} />
                              ) : (
                                <CheckIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: activeConfig.bgLight,
                            color: activeConfig.color,
                          }}
                        >
                          {React.cloneElement(activeConfig.icon, { sx: { fontSize: 22 } })}
                        </Avatar>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            )
          })}
        </Grid>
      )}

      {/* ════════════ Detail Dialog ════════════ */}
      <Dialog
        open={dialogOpen && !claimerDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {selectedPage && (() => {
          const requestedLabel = selectedPage.createdAt ? formatAbsoluteDate(selectedPage.createdAt) : ''
          return (
          <>
            {/* Dialog header with colored banner */}
            <Box
              sx={{
                background: activeConfig.gradient,
                px: 3,
                py: 2.5,
                color: 'white',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, minWidth: 0, pr: 2 }}>
                  {selectedPage.name || 'Unnamed Page'}
                </Typography>
                <IconButton onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.8)', mt: -0.5, mr: -0.5, flexShrink: 0 }}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              {((selectedPage.city || selectedPage.state) || selectedPage.requesterEmail) && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ gap: 2 }}>
                  {(selectedPage.city || selectedPage.state) ? (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                      <LocationOnIcon sx={{ fontSize: 15, opacity: 0.8, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {[selectedPage.city, selectedPage.state].filter(Boolean).join(', ')}
                      </Typography>
                    </Stack>
                  ) : (
                    <Box />
                  )}
                  {selectedPage.requesterEmail && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexShrink: 1, justifyContent: 'flex-end' }}>
                      <EmailIcon sx={{ fontSize: 15, opacity: 0.85, flexShrink: 0 }} />
                      <Link
                        component="button"
                        type="button"
                        onClick={() => handleOpenClaimerDetails(selectedPage)}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textDecoration: 'underline',
                          textUnderlineOffset: 3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '&:hover': { opacity: 0.9 },
                        }}
                      >
                        {selectedPage.requesterEmail}
                      </Link>
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>

            <DialogContent sx={{ p: 0 }}>
              <Stack spacing={0} divider={<Divider />}>
                {/* Page ID */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TagIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                    <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                      Page ID
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A2027' }}>
                    {selectedPage.id}
                  </Typography>
                </Stack>

                {requestedLabel && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AccessTimeIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                      <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                        Requested
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A2027' }}>
                      {requestedLabel}
                    </Typography>
                  </Stack>
                )}

                {/* Name */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                    <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                      Name
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: '#1A2027', maxWidth: '60%', textAlign: 'right' }}
                  >
                    {selectedPage.name || '—'}
                  </Typography>
                </Stack>

                {/* City */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LocationOnIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                    <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                      City
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A2027' }}>
                    {selectedPage.city || '—'}
                  </Typography>
                </Stack>

                {/* State */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LocationOnIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                    <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                      State
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A2027' }}>
                    {selectedPage.state || '—'}
                  </Typography>
                </Stack>

                {/* Claim ID */}
                {selectedPage.claimId != null && selectedPage.claimId > 0 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AssignmentIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                      <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                        Claim ID
                      </Typography>
                    </Stack>
                    <Chip
                      label={`#${selectedPage.claimId}`}
                      size="small"
                      sx={{
                        bgcolor: `${activeConfig.color}12`,
                        color: activeConfig.color,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                )}

                {/* Type */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AssignmentIcon sx={{ fontSize: 18, color: '#90A4AE' }} />
                    <Typography variant="body2" sx={{ color: '#78909C', fontWeight: 500 }}>
                      Type
                    </Typography>
                  </Stack>
                  <Chip
                    label={entityLabel}
                    size="small"
                    sx={{
                      bgcolor: isAgency ? '#E0F7FA' : '#E0F2F1',
                      color: isAgency ? '#00838F' : '#00695C',
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Stack>
            </DialogContent>

            {statusTab === 'claimed' && (
              <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={
                    actionLoading?.pageId === selectedPage.id && actionLoading?.action === 'reject' ? (
                      <CircularProgress size={16} color="error" />
                    ) : (
                      <CancelIcon />
                    )
                  }
                  onClick={() => handleReject(selectedPage.id)}
                  disabled={actionLoading?.pageId === selectedPage.id}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1 }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    actionLoading?.pageId === selectedPage.id && actionLoading?.action === 'approve' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  onClick={() => handleApprove(selectedPage.id)}
                  disabled={actionLoading?.pageId === selectedPage.id}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1, boxShadow: 'none' }}
                >
                  Approve
                </Button>
              </DialogActions>
            )}
          </>
          )
        })()}
      </Dialog>

      {/* Claimer user details */}
      <Dialog
        open={claimerDialogOpen}
        onClose={handleCloseClaimerDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Claimer Details
            </Typography>
            <IconButton onClick={handleCloseClaimerDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {claimerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {claimerError && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {claimerError}
                </Alert>
              )}

              {claimerUser && (
                <>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={claimerUser.picture || undefined}
                      alt={claimerUser.name}
                      sx={{ width: 48, height: 48, bgcolor: '#B0BEC5' }}
                    >
                      {(claimerUser.name || claimerUser.email || '?')
                        .split(/\s+/)
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {claimerUser.name || '—'}
                      </Typography>
                      {claimerUser.email && (
                        <Link href={`mailto:${claimerUser.email}`} variant="body2" sx={{ fontWeight: 600 }}>
                          {claimerUser.email}
                        </Link>
                      )}
                    </Box>
                  </Stack>
                  {claimerUser.position_title && (
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <BadgeIcon sx={{ fontSize: 20, color: '#90A4AE', mt: 0.25 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 600 }}>
                          Position
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {claimerUser.position_title}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  {claimerUser.nursing_specialization && (
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <WorkIcon sx={{ fontSize: 20, color: '#90A4AE', mt: 0.25 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 600 }}>
                          Specialization
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {claimerUser.nursing_specialization}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  {claimerUser.permissions?.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {claimerUser.permissions.map((perm) => (
                        <Chip key={perm} label={perm} size="small" sx={{ fontWeight: 600 }} />
                      ))}
                    </Stack>
                  )}
                </>
              )}

              {selectedPage && (() => {
                const proofLink = getClaimProofLink(selectedPage.proofDocumentation)
                const hasClaimFields =
                  !!(selectedPage.organizationName || selectedPage.name || selectedPage.requesterName || selectedPage.requesterEmail || proofLink)
                if (!hasClaimFields) return null
                return (
                  <>
                    {(claimerUser || claimerError) && <Divider />}
                    {(selectedPage.organizationName || selectedPage.name) && (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <AssignmentIcon sx={{ fontSize: 20, color: '#90A4AE', mt: 0.25 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 600 }}>
                            Organization Name
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {selectedPage.organizationName || selectedPage.name}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                    {selectedPage.requesterName && (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <PersonIcon sx={{ fontSize: 20, color: '#90A4AE', mt: 0.25 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 600 }}>
                            Your Full Name
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {selectedPage.requesterName}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                    {selectedPage.requesterEmail && (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <EmailIcon sx={{ fontSize: 20, color: '#90A4AE', mt: 0.25 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 600 }}>
                            Organization Email
                          </Typography>
                          <Link href={`mailto:${selectedPage.requesterEmail}`} sx={{ fontWeight: 700, display: 'block' }}>
                            {selectedPage.requesterEmail}
                          </Link>
                        </Box>
                      </Stack>
                    )}
                    {proofLink && (
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <OpenInNewIcon sx={{ fontSize: 20, color: '#90A4AE', mt: 0.25 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 600 }}>
                            Proof of Employment
                          </Typography>
                          <Link
                            href={proofLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: 600, display: 'block', wordBreak: 'break-all' }}
                          >
                            {proofLink}
                          </Link>
                        </Box>
                      </Stack>
                    )}
                  </>
                )
              })()}

              {!claimerUser &&
                !claimerLoading &&
                !claimerError &&
                !selectedPage?.requesterName &&
                !selectedPage?.requesterEmail &&
                !(selectedPage?.organizationName || selectedPage?.name) && (
                  <Typography variant="body2" sx={{ color: '#78909C' }}>
                    No claim details available.
                  </Typography>
                )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseClaimerDialog} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ borderRadius: 2, fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ClaimPagesDashboard
